import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, timer, filter, tap, retry, from, concatMap, toArray, switchMap } from 'rxjs';
import { SparePartListResponse, SparePartService } from '../../../../services/spare-part.service';
import * as SparePartActions from './spare-part.actions';
import type {
  SparePartFlowPreviousReturnTo,
  SparePartFlowReturnTo,
} from './spare-part.actions';
import { SparePartResponse } from '../../../../api/gse/models/spare-part-response';
import { StrictHttpResponse } from '../../../../api/gse/strict-http-response';
import { Router } from '@angular/router';
import { APP_PATHS } from '../../../../core/constants/platform-config.constants';
import { ToastActions } from '../../../toast/toast.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { GetSparePartsResponse, SparePartDto } from '../../../../api/gse/models';
import { SparePartDetailDto } from '../../../../api/gse/models/spare-part-detail-dto';
import { SparePartDetailResponse } from '../../../../api/gse/models/spare-part-detail-response';

@Injectable()
export class SparePartEffects {
  private actions$ = inject(Actions);
  private sparePartService = inject(SparePartService);
  private router = inject(Router);
  private location = inject(Location);

  loadSpareParts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadSpareParts),
      mergeMap(({ params }) => {
        return this.sparePartService.loadSpareParts(params).pipe(
          map((response) => {
            const apiResponse = response.body;
            const mappedResponse = {
              data: apiResponse?.data || [],
              totalRecords: apiResponse.page?.totalElements || 0,
            };
            return mappedResponse;
          }),
          map((mappedResponse) =>
            SparePartActions.loadSparePartsSuccess({
              response: mappedResponse as SparePartListResponse,
            })
          ),
          catchError((error) => {
            return of(SparePartActions.loadSparePartsFailure({ error }));
          })
        );
      })
    )
  );

  loadSparePartById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadSparePartById),
      mergeMap(({ id }) =>
        this.sparePartService.getSparePart(id).pipe(
          map((response) => {
            const data = response.body.data;
            const sparePart = Array.isArray(data) ? data[0] : data;
            if (!sparePart) {
              throw new Error('Spare Part not found');
            }
            return SparePartActions.loadSparePartByIdSuccess({
              sparePart: sparePart as SparePartDetailDto,
            });
          }),
          catchError((error) => of(SparePartActions.loadSparePartByIdFailure({ error })))
        )
      )
    )
  );

  createInventoryCorrection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.createInventoryCorrection),
      mergeMap(({ body }) =>
        this.sparePartService.createInventoryCorrection(body).pipe(
          switchMap(() => this.sparePartService.getSparePart(body.spareId!)),
          mergeMap((response) => {
            const detailBody = response.body as SparePartDetailResponse;
            const part = Array.isArray(detailBody?.data) ? detailBody.data[0] : detailBody?.data;
            if (!part) {
              return of(
                ToastActions.showErrorToast({
                  summary: 'Request Failed',
                  detail: 'Stock correction submitted but failed to refresh spare part.',
                })
              );
            }
            return from([
              SparePartActions.loadSparePartByIdSuccess({ sparePart: part as SparePartDetailDto }),
              ToastActions.showSuccessToast({
                summary: 'Approval Requested',
                detail: 'Stock correction request has been sent for approval.',
              }),
            ]);
          }),
          catchError((error) =>
            from([
              SparePartActions.createInventoryCorrectionFailure({ error }),
              ToastActions.showErrorToast({
                summary: 'Request Failed',
                detail:
                  (error?.error?.message ?? error?.message) ||
                  'Failed to submit stock correction request.',
              }),
            ])
          )
        )
      )
    )
  );

  createSparePart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.createSparePart),
      mergeMap(({ body, images, attachments, returnTo, previousReturnTo }) =>
        this.sparePartService.createSparePart(body).pipe(
          map((response: StrictHttpResponse<SparePartResponse>) =>
            SparePartActions.createSparePartSuccess({
              response: response.body,
              images,
              attachments,
              returnTo,
              previousReturnTo,
            })
          ),
          catchError((error) =>
            of(
              SparePartActions.createSparePartFailure({
                error: error,
              })
            )
          )
        )
      )
    )
  );

  updateSparePart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.updateSparePart),
      mergeMap(
        ({
          id,
          body,
          images,
          attachments,
          markedForDeletionUuids,
          isStockUpdated,
          returnTo,
          previousReturnTo,
        }) => {
          const uuids = (markedForDeletionUuids || []).filter((u) => !!u);

          const deleteAll$ = uuids.length
            ? from(uuids).pipe(
              concatMap((uuid) =>
                this.sparePartService
                  .deleteSparePartAttachmentById(id, uuid)
                  .pipe(
                    retry({ count: 2, delay: 500 }),
                    catchError(() => of(null))
                  )
              ),
              toArray(),
              map(() => null)
            )
            : of(null);

          return deleteAll$.pipe(
            mergeMap(() => this.sparePartService.updateSparePart(id, body)),
            map((response: StrictHttpResponse<SparePartResponse>) =>
              SparePartActions.updateSparePartSuccess({
                response: response.body,
                images,
                attachments,
                isStockUpdated,
                returnTo,
                previousReturnTo,
              })
            ),
            catchError((error) =>
              of(
                SparePartActions.updateSparePartFailure({
                  error: error,
                })
              )
            )
          );
        })
    )
  );

  triggerUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.createSparePartSuccess, SparePartActions.updateSparePartSuccess),
      map((action) => {
        const isStockUpdated = (action as { isStockUpdated?: boolean }).isStockUpdated ?? false;
        const { response, images, attachments } = action;
        const returnTo =
          'returnTo' in action ? (action as { returnTo?: SparePartFlowReturnTo }).returnTo : undefined;
        const previousReturnTo =
          'previousReturnTo' in action
            ? (action as { previousReturnTo?: SparePartFlowPreviousReturnTo }).previousReturnTo
            : undefined;
        const id = response.data?.[0]?.id;
        if (id && ((images && images.length > 0) || (attachments && attachments.length > 0))) {
          return SparePartActions.uploadFiles({
            id,
            images: images || [],
            attachments: attachments || [],
            isStockUpdated,
            returnTo,
            previousReturnTo,
          });
        }
        return { type: 'NOOP' };
      })
    ),
  );

  uploadFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.uploadFiles),
      mergeMap(({ id, images, attachments, isStockUpdated, returnTo, previousReturnTo }) =>
        this.sparePartService.uploadSparePartFiles(id, images, attachments).pipe(
          map((response: SparePartResponse) =>
            SparePartActions.uploadFilesSuccess({
              response,
              isStockUpdated,
              returnTo,
              previousReturnTo,
            })
          ),
          retry({
            count: 3,
            delay: () => timer(3000),
          }),
          catchError((error) =>
            of(
              SparePartActions.uploadFilesFailure({
                error: error,
                returnTo,
                previousReturnTo,
              })
            )
          )
        )
      )
    )
  );

  handleSubmissionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SparePartActions.createSparePartSuccess,
          SparePartActions.updateSparePartSuccess,
          SparePartActions.uploadFilesSuccess
        ),
        filter((action) => {
          if (
            action.type === SparePartActions.createSparePartSuccess.type ||
            action.type === SparePartActions.updateSparePartSuccess.type
          ) {
            const files =
              (action.images?.length || 0) + (action.attachments?.length || 0);
            return files === 0;
          }
          return true;
        }),
        tap(
          (
            action:
              | ReturnType<typeof SparePartActions.createSparePartSuccess>
              | ReturnType<typeof SparePartActions.updateSparePartSuccess>
              | ReturnType<typeof SparePartActions.uploadFilesSuccess>
          ) => {
            const data = action.response?.data;
            const sparePartId = Array.isArray(data)
              ? data[0]?.id
              : (data as SparePartDto | undefined)?.id;

            let returnTo: SparePartFlowReturnTo | undefined;
            let previousReturnTo: SparePartFlowPreviousReturnTo | undefined;

            if (action.type === SparePartActions.uploadFilesSuccess.type) {
              returnTo = action.returnTo;
              previousReturnTo = action.previousReturnTo;
            } else if (
              action.type === SparePartActions.createSparePartSuccess.type ||
              action.type === SparePartActions.updateSparePartSuccess.type
            ) {
              returnTo = action.returnTo;
              previousReturnTo = action.previousReturnTo;
            }

            if (sparePartId) {
              const finalReturnTo =
                returnTo === 'details' ? previousReturnTo : returnTo;
              this.router.navigate(
                ['/', APP_PATHS.GSE, APP_PATHS.INVENTORY, APP_PATHS.SPARE_PARTS, sparePartId],
                {
                  state: { returnTo: finalReturnTo || 'listing' },
                }
              );
            } else {
              this.router.navigate([
                '/',
                APP_PATHS.GSE,
                APP_PATHS.INVENTORY,
                APP_PATHS.SPARE_PARTS,
              ]);
            }
          }
        ),
        map(
          (
            action:
              | ReturnType<typeof SparePartActions.createSparePartSuccess>
              | ReturnType<typeof SparePartActions.updateSparePartSuccess>
              | ReturnType<typeof SparePartActions.uploadFilesSuccess>
          ) => {
            const apiMessage = action.response?.message;

            return ToastActions.showSuccessToast({
              summary: 'Success',
              detail: apiMessage || 'Spare Part Saved Successfully',
            });
          }
        )
      ),
    { dispatch: true }
  );

  handleSubmissionPartialSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SparePartActions.uploadFilesFailure),
        tap(() => {
          // On file upload failure, navigate back to spare parts listing.
          this.router.navigate(['/', APP_PATHS.GSE, APP_PATHS.INVENTORY, APP_PATHS.SPARE_PARTS]);
        }),
        map(() =>
          ToastActions.showWarnToast({
            summary: 'Partial Success',
            detail: 'Spare part added successfully, but image and attachment upload failed',
          })
        )
      ),
    { dispatch: true }
  );

  handleSubmissionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SparePartActions.createSparePartFailure, SparePartActions.updateSparePartFailure),
        map(({ error }) => {
          let errorMessage = 'Failed to save spare part';
          if (error instanceof HttpErrorResponse) {
            errorMessage =
              (error.error as { message?: string })?.message || error.message || errorMessage;
          } else if (error && typeof error === 'object' && 'message' in error) {
            errorMessage = (error as { message: string }).message;
          }

          return ToastActions.showErrorToast({
            summary: 'Error',
            detail: errorMessage,
          });
        })
      ),
    { dispatch: true }
  );

  // Keep existing effects
  loadStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadStats),
      mergeMap(() =>
        this.sparePartService.getStats().pipe(
          map((stats) => SparePartActions.loadStatsSuccess({ stats })),
          catchError((error) => of(SparePartActions.loadStatsFailure({ error })))
        )
      )
    )
  );

  loadAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadAlerts),
      mergeMap(() =>
        this.sparePartService.getAlerts().pipe(
          map((alerts) => SparePartActions.loadAlertsSuccess({ alerts })),
          catchError((error) => of(SparePartActions.loadAlertsFailure({ error })))
        )
      )
    )
  );

  loadMaintenances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadMaintenances),
      mergeMap(() =>
        this.sparePartService.getMaintenances().pipe(
          map((maintenances) => SparePartActions.loadMaintenancesSuccess({ maintenances })),
          catchError((error) => of(SparePartActions.loadMaintenancesFailure({ error })))
        )
      )
    )
  );

  loadStatusOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadStatusOptions),
      mergeMap(() =>
        this.sparePartService.getStatusOptions().pipe(
          map((statusOptions) => SparePartActions.loadStatusOptionsSuccess({ statusOptions })),
          catchError((error) => of(SparePartActions.loadStatusOptionsFailure({ error })))
        )
      )
    )
  );

  loadHealthStockTrend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadHealthStockTrend),
      switchMap(({ startDate, endDate }) =>
        this.sparePartService.getHealthStockTrend({ startDate, endDate }).pipe(
          map((response) => {
            const data = (response as any)?.body?.data ?? [];
            return SparePartActions.loadHealthStockTrendSuccess({
              data: Array.isArray(data) ? data : [],
            });
          }),
          catchError((error) =>
            of(SparePartActions.loadHealthStockTrendFailure({ error }))
          )
        )
      )
    )
  );

  exportSpareParts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.exportSpareParts),
      mergeMap(({ params }) =>
        this.sparePartService.loadSpareParts(params).pipe(
          mergeMap(async (response: StrictHttpResponse<GetSparePartsResponse>) => {
            try {
              const [utils, FileSaver] = await Promise.all([import('xlsx'), import('file-saver')]);

              const data: SparePartDto[] = response.body.data || [];
              const exportData = data.map((item: SparePartDto) => ({
                Name: item.name || item.description || '',
                Category: item.category?.name || '',
                'Model Number': item.modelNo || '',
                Location:
                  item.locations
                    ?.map((l) => l.name || `WH:${l.id}`)
                    .join(', ') || '',
                'Current Stock': item.stock || 0,
                Unit: item.unit?.name || '',
                'Min Quantity': item.minStock || 0,
                'Max Quantity': item.maxStock || 0,
              }));

              const worksheet = utils.utils.json_to_sheet(exportData);
              const workbook = { Sheets: { SpareParts: worksheet }, SheetNames: ['SpareParts'] };
              const excelBuffer: ArrayBuffer = utils.write(workbook, {
                bookType: 'xlsx',
                type: 'array',
              });

              const EXCEL_TYPE =
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
              const EXCEL_EXTENSION = '.xlsx';
              const blob: Blob = new Blob([excelBuffer], { type: EXCEL_TYPE });
              FileSaver.saveAs(
                blob,
                'spare_parts_Inventory_export_' + new Date().getTime() + EXCEL_EXTENSION
              );

              return SparePartActions.exportSparePartsSuccess();
            } catch (error) {
              return SparePartActions.exportSparePartsFailure({ error: error as HttpErrorResponse });
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(SparePartActions.exportSparePartsFailure({ error }))
          )
        )
      )
    )
  );

  loadCorrectionLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadCorrectionLogs),
      mergeMap(({ sparePartId, next, size, sort }) =>
        this.sparePartService.getInventoryCorrectionLogsBySparePart({
          id: sparePartId,
          next,
          size: size || 10,
          sort: sort || 'desc',
        }).pipe(
          map((response) => {
            const logs = response.body?.data || [];
            const nextCursor = response.body?.next;
            return SparePartActions.loadCorrectionLogsSuccess({ logs, next: nextCursor });
          }),
          catchError((error) =>
            of(SparePartActions.loadCorrectionLogsFailure({ error }))
          )
        )
      )
    )
  );

  loadMoreCorrectionLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadMoreCorrectionLogs),
      mergeMap(({ sparePartId, next, size, sort }) =>
        this.sparePartService.getInventoryCorrectionLogsBySparePart({
          id: sparePartId,
          next,
          size: size || 10,
          sort: sort || 'desc',
        }).pipe(
          map((response) => {
            const logs = response.body?.data || [];
            const nextCursor = response.body?.next;
            return SparePartActions.loadMoreCorrectionLogsSuccess({ logs, next: nextCursor });
          }),
          catchError((error) =>
            of(SparePartActions.loadMoreCorrectionLogsFailure({ error }))
          )
        )
      )
    )
  );

  loadSparePartLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadSparePartLogs),
      switchMap(({ sparePartId, next, size, sort }) =>
        this.sparePartService.getSparePartLogs({ id: sparePartId, next, size, sort }).pipe(
          map((response) => {
            const data = response.body?.data || [];
            const nextCursor = response.body?.next;
            return SparePartActions.loadSparePartLogsSuccess({
              logs: data,
              next: nextCursor,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(SparePartActions.loadSparePartLogsFailure({ error }))
          )
        )
      )
    )
  );

  loadMoreSparePartLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SparePartActions.loadMoreSparePartLogs),
      switchMap(({ sparePartId, next, size, sort }) =>
        this.sparePartService.getSparePartLogs({ id: sparePartId, next, size, sort }).pipe(
          map((response) => {
            const data = response.body?.data || [];
            const nextCursor = response.body?.next;
            return SparePartActions.loadMoreSparePartLogsSuccess({
              logs: data,
              next: nextCursor,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(SparePartActions.loadMoreSparePartLogsFailure({ error }))
          )
        )
      )
    )
  );

  constructor() { }
}
